/**
 * InspectionStatusIndicatorBadge — shared status catalog + parent-card mapping.
 *
 * The badge reports a single checkpoint's overall status. The six values map 1:1
 * to the Figma "Checkpoint Status" variant axis (node 46998:193150) and each value
 * drives both the label and the colour together.
 */

/** Supported `checkpointStatus` values (prop validator + Storybook options). */
export const CHECKPOINT_STATUSES = [
  "Not Started",
  "In Progress",
  "Passed",
  "Failed",
  "Reset",
  "Not Applicable",
];

/** Status -> fixed label. Casing is exactly as designed — note the lowercase "in". */
export const CHECKPOINT_STATUS_LABELS = {
  "Not Started": "Checkpoint Not Started",
  "In Progress": "Checkpoint in Progress",
  Passed: "Checkpoint Passed",
  Failed: "Checkpoint Failed",
  Reset: "Checkpoint Reset",
  "Not Applicable": "Checkpoint Not Applicable",
};

/**
 * Parent card (AsEmployeeCheckpointInspectionCard) status -> coarse badge value.
 *
 * The card names its states finely (it distinguishes a failed quiz from a failed
 * inspection) while the badge reports only the coarse outcome, so several card
 * statuses collapse into one badge value. Extracted from all 38 card variants on
 * 2026-09-01 and applied consistently.
 *
 * Inspector-only card statuses ("Awaiting inspection", "Inspection passed",
 * "Inspection failed") intentionally have NO entry: inspector-only checkpoints
 * never render this badge — their MetaRow opens with InspectorOnlyBadge instead.
 */
export const CARD_STATUS_TO_CHECKPOINT_STATUS = {
  "Not started": "Not Started",
  "Quiz not attempted": "In Progress",
  "Inspection ready": "In Progress",
  "Quiz passed / inspection pending": "In Progress",
  "Quiz failed": "Failed",
  "Inspection failed": "Failed",
  Complete: "Passed",
  Passed: "Passed",
  Reset: "Reset",
  "Not Applicable": "Not Applicable",
};

/** Case/whitespace-insensitive lookup for a card status; null when unmapped. */
export function checkpointStatusForCardStatus(cardStatus) {
  const normalized = String(cardStatus || "")
    .trim()
    .toLowerCase();
  if (!normalized) return null;

  const entry = Object.entries(CARD_STATUS_TO_CHECKPOINT_STATUS).find(
    ([card]) => card.toLowerCase() === normalized
  );
  return entry ? entry[1] : null;
}
